import { AdSize, PublisherAPI, PublisherSlotAPI, SystemEvent, VastUrlConfig, VastUrlResponse } from '@mbrtargeting/metatag-shared-types/metatag-core';
import { generateId, getBrowserDetails, isBoolean, isString, isStringArray } from '@mbrtargeting/metatag-utils';
import { inject, injectionTarget } from '../../../loader/decorators/inject.js';
import { triggerEvent, waitForEvent, waitForEventPromise } from '../../../loader/essentials/events.js';
import { MetaTag } from '../../../loader/essentials/init-metatag.js';
import { logger } from '../../../loader/essentials/logger.js';
import { AdslotRegistry } from '../../../loader/modules/adslot-registry.js';
import { ConsentCache } from '../../../loader/modules/consent-cache.js';
import { ADSLOT_REGISTRY, CONSENT_CACHE, METATAG } from '../../../loader/token.js';
import { ADSLOT_CONTROLLER, FORMAT_CONTROLLER, GLOBAL_SETTINGS, GLOBAL_TARGETING, VAST_TAG_GENERATOR } from '../../token.js';
import { AdslotControllerInterface } from '../adslot-controller/adslot-controller-interface.js';
import { Adslot } from '../adslot-controller/adslot.js';
import { FormatController } from '../format-controller/format-controller.js';
import { sendToGpt } from '../gam-adserver/gpt-helper.js';
import { GlobalSettings } from '../global-settings/global-settings.js';
import { GlobalTargeting } from '../global-targeting/global-targeting.js';
import { VastTagGenerator } from '../video/vast-tag-generator.js';

const log = logger.logGroup({ prefix: 'Publisher' });

@injectionTarget()
export class FullPublisherAPI implements PublisherAPI {

    @inject(METATAG) private metaTag!: MetaTag;
    @inject(ADSLOT_REGISTRY) private adslotRegistry!: AdslotRegistry;
    @inject(ADSLOT_CONTROLLER) private adslotController!: AdslotControllerInterface;
    @inject(GLOBAL_SETTINGS) private globalSettings!: GlobalSettings;
    @inject(GLOBAL_TARGETING) private globalTargeting!: GlobalTargeting;
    @inject(FORMAT_CONTROLLER) private formatController!: FormatController;
    @inject(VAST_TAG_GENERATOR) private vastTagGenerator!: VastTagGenerator;
    @inject(CONSENT_CACHE) private consentCache!: ConsentCache;

    constructor() {
        this.metaTag.Publisher = this;
        /**
         * Reload all slots if browser loads website from back/forward cache. Most often affects mobile iOS devices and Safari Desktop
         */
        window.addEventListener('pageshow', (event) => {
            if (event.persisted) {
                this.addKeyValue('env', '2');
                this.loadAllSlots(true);
            }
        });
    }

    public registerSlot(slotName: string, container: string | HTMLElement): PublisherSlotAPI | null {
        return this.adslotRegistry.createAdslot(slotName, container);
    }

    public unregisterSlot(slotName: string, deleteAd?: boolean | undefined): boolean {
        this.adslotRegistry.deleteAdslot(slotName);
        return true;
    }

    public unregisterAllSlots(deleteAds?: boolean | undefined): boolean {
        this.adslotRegistry.deleteAllAdslots();
        return true;
    }

    public registerModularSlot(slotName: string, container: string | HTMLElement, width: number, height: number, adSizes: AdSize[], isMobile: boolean, useSubzone: string): PublisherSlotAPI | null {
        return this.adslotRegistry.createModularAdslot(slotName, container, width, height, adSizes, isMobile, useSubzone);
    }

    public loadAllSlots(reloadAds: boolean | undefined = false): boolean {
        if (!isBoolean(reloadAds)) return false;
        const slotNames: string[] = (this.adslotRegistry.getAdslots() as Adslot[]).map(adslot => adslot.slotName);
        this.adslotController.markLoadIntention(slotNames, {
            forceReload: reloadAds,
            runAuction: true,
        });
        return true;
    }

    public loadMultipleSlots(slotNames: string[]): boolean {
        if (!isStringArray(slotNames)) return false;
        this.adslotController.markLoadIntention(slotNames, {
            forceReload: true,
            runAuction: true,
        });
        return true;
    }

    public loadSlot(slotName: string): boolean {
        if (!isString(slotName)) return false;
        this.adslotController.markLoadIntention([slotName], {
            forceReload: true,
            runAuction: true,
        });
        return true;
    }

    public finalizeSlots(): boolean {
        log.debug('finalizeSlots');
        triggerEvent(SystemEvent.SDG_SLOTS_FINALIZED, {});
        return true;
    }

    public setZone(zone: string): boolean {
        if (!isString(zone) || zone === '') return false;
        this.globalSettings.setZone(zone);
        return true;
    }

    public addSubZone(subZone: string): boolean {
        if (!isString(subZone)) return false;
        this.globalSettings.setSubZone(subZone);
        return true;
    }

    public setPageType(pageType: string): boolean {
        if (!isString(pageType)) return false;
        this.globalSettings.setPageType(pageType);
        return true;
    }

    public addKeywords(keywords: string[]): boolean {
        if (!isStringArray(keywords)) return false;
        this.globalTargeting.addValues('keywords', keywords);
        return true;
    }

    public addKeyword(keyword: string): boolean {
        if (!isString(keyword)) return false;
        this.globalTargeting.addValues('keywords', [keyword]);
        return true;
    }

    public removeKeywords(keywords: string[]): boolean {
        if (!isStringArray(keywords)) return false;
        this.globalTargeting.deleteValues('keywords', keywords);
        return true;
    }

    public removeKeyword(keyword: string): boolean {
        if (!isString(keyword)) return false;
        this.globalTargeting.deleteValues('keywords', [keyword]);
        return true;
    }

    public addKeyValue(key: string, value: string): boolean {
        if (!isString(key) || !isString(value)) return false;
        this.globalTargeting.addValues(key, [value]);
        return true;
    }

    public addKeyValues(keyValues: Record<string, string[]>): boolean {
        for (const [key, values] of Object.entries(keyValues).filter(([key, values]) => isString(key) && isStringArray(values))) {
            this.globalTargeting.addValues(key, values);
        }
        return true;
    }

    public removeKeyValue(key: string): boolean {
        if (!isString(key)) return false;
        this.globalTargeting.delete(key);
        return true;
    }

    public removeKeyValues(keys: string[]): boolean {
        if (!isStringArray(keys)) return false;
        for (const key of keys) {
            this.globalTargeting.delete(key);
        }
        return true;
    }

    public blockFormats(blockedFormats: string[]): boolean {
        this.formatController.blockFormats(blockedFormats);
        return true;
    }

    public resetFormatBlocks(): boolean {
        this.formatController.resetFormatBlocks();
        return true;
    }

    public getCustomVendorConsent(vendorName: string): boolean {
        log.deprecated('getCustomVendorConsent("%s") is deprecated. Please use __tcfapi instead.', [vendorName]);
        return this.consentCache.getCustomVendorConsent(vendorName);
    }

    public getIABVendorConsent(vendorId: number, callback: (hasConsent: boolean) => void): void {
        log.deprecated('getIABVendorConsent is deprecated. Please use __tcfapi instead.');
        this.consentCache.whenConsentConditions([{ consentedVendors: [vendorId] }]).then(callback);
    }

    public setAdServerConsent(consent: boolean): void {
        this.globalSettings.setAdServerConsent(consent);
    }

    /**
     * not used anymore. All adserver calls are using HTTPS as default.
     * However, still present on some websites.
     */
    public activateSecureProtocol(): boolean {
        log.error('SDG.Publisher.activateSecureProtocol() is not used anymore. All communication is using HTTPS as default. Please remove this method from your website.');
        return false;
    }

    public transitionAdvertisements(): void {
        //maybe later on, allow configuration via object {desktop: false, mobile: false}
        log.debug('requesting transitionAdvertisements');
        const { isPhone, isTablet } = getBrowserDetails();
        const mobileDevice = isPhone || isTablet;
        const slotName = `${(mobileDevice) ? 'mobile' : 'desktop'}_transitionAd`;

        //register only after at least 1 gpt adslot has been defined/called. Implementation from Google will not work otherwise
        waitForEvent(SystemEvent.TRACKING_PHASE_STARTED, () => {
            this.registerSlot(slotName, generateId())?.load();
        });
    }

    public generateVastUrls(configOrString: string | VastUrlConfig, videoZone?: string | undefined): VastUrlResponse {
        return this.vastTagGenerator.generateVastUrls(configOrString, videoZone);
    }

    public getVastAdDataObject(configOrString: string | VastUrlConfig, videoZone?: string | undefined): VastUrlResponse {
        return this.generateVastUrls(configOrString, videoZone);
    }

    public async generateVastUrlsPromise(config: VastUrlConfig): Promise<VastUrlResponse> {
        // currently assuming that we collect all ids in the TARGETING phase, so we wait for DELIVERY phase
        await waitForEventPromise(SystemEvent.DELIVERY_PHASE_STARTED);
        return this.vastTagGenerator.generateVastUrls(config);
    }

    public getAdSlot(slotName: string): PublisherSlotAPI | null {
        return this.adslotRegistry.getAdslot(slotName);
    }

    /**
     * @interface PublisherAPI
     * @inheritdoc
     */
    public transmitAttStatus(status: string): void {
        log.debug('transmitAttStatus("%s")', [status]);
        switch (status?.toString().toLowerCase()) {
            case 'restricted':
            case 'denied':
                sendToGpt((_gpt, pubads) => {
                    pubads.setPrivacySettings({
                        nonPersonalizedAds: true,
                    });
                });
        }
    }
}
